import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';
import { getJobFilters } from '../../../../utils/api_service/JobPosting';
import { getOrganizationTree } from '../../../../utils/api_service/organization_unit';
import type { FilterOption, FilterState } from '../../../../utils/types/JobOpenings';
import type { IOrganizationUnitNode } from '../interfaces';

export interface IUseJobPostingFilter {
  jobTitles: string[];
  locations: string[];
  departments: string[];
  workTypeOptions: { label: string; value: string }[];
  handleTitleLocationChange: (group: 'jobTitle' | 'location', value: string | null) => void;
  handleWorkTypeChange: (values: string[]) => void;
  handleDepartmentChange: (values: string[]) => void;
  handleClearFilters: () => void;
  handleSearch: () => void;
}

interface IUseJobPostingFilterParams {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  setSearch: (value: boolean) => void;
  search: boolean;
}

const toLabelValue = (item: FilterOption | string): { label: string; value: string } =>
  typeof item === 'string' ? { label: item, value: item } : { label: item.label, value: item.value };

const collectUnitNames = (organization: IOrganizationUnitNode): string[] => {
  const unitNames: string[] = [];
  const traverse = (node: IOrganizationUnitNode): void => {
    unitNames.push(node.unitName);
    node.children?.forEach((child) => traverse(child));
  };
  traverse(organization);
  return unitNames;
};

export const useJobPostingFilter = ({
  filters,
  setFilters,
  setSearch,
  search,
}: IUseJobPostingFilterParams): IUseJobPostingFilter => {
  const { t } = useTranslation(NS.jobPortal);
  const [departments, setDepartments] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [jobTitles, setJobTitles] = useState<string[]>([]);

  const workTypeOptions = useMemo(
    () => [
      { label: t(JOB_PORTAL_KEYS.filter.hybrid), value: 'Hybrid' },
      { label: t(JOB_PORTAL_KEYS.filter.remote), value: 'Remote' },
      { label: t(JOB_PORTAL_KEYS.filter.office), value: 'Office' },
    ],
    [t],
  );

  useEffect(() => {
    const fetchJobFilters = async (): Promise<void> => {
      const response = await getJobFilters();
      const responseData = await response.json();
      setLocations((responseData.location as (FilterOption | string)[]).map((item) => toLabelValue(item).value));
      setJobTitles((responseData.jobTitle as (FilterOption | string)[]).map((item) => toLabelValue(item).value));
    };
    void getOrganizationTree().then((response) => {
      setDepartments(collectUnitNames(response as IOrganizationUnitNode));
    });
    void fetchJobFilters();
  }, []);

  const handleTitleLocationChange = (group: 'jobTitle' | 'location', value: string | null): void => {
    setFilters({
      ...filters,
      [group]: value ? [value] : [],
    });
  };

  const handleWorkTypeChange = (values: string[]): void => {
    setFilters((prev) => ({ ...prev, workType: values }));
  };

  const handleDepartmentChange = (values: string[]): void => {
    setFilters((prev) => ({ ...prev, department: values }));
  };

  const handleClearFilters = (): void => {
    setFilters({ jobTitle: [], location: [], workType: [], department: [] });
    setSearch(!search);
  };

  const handleSearch = (): void => {
    setSearch(!search);
  };

  return {
    jobTitles,
    locations,
    departments,
    workTypeOptions,
    handleTitleLocationChange,
    handleWorkTypeChange,
    handleDepartmentChange,
    handleClearFilters,
    handleSearch,
  };
};
